import { Loader2, Network } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  TopEntityThumbnail,
  variantForSeed,
} from '@/components/investigations/InsightThumbnailPlaceholder'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { GraphStats, IdentityDetail } from '@/features/identities/types'

export interface GraphEntryCardProps {
  identity: IdentityDetail
  graphStats: GraphStats
  opening: boolean
  onOpen: () => void
}

export function GraphEntryCard({ identity, graphStats, opening, onOpen }: GraphEntryCardProps) {
  const { t } = useTranslation()

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className="bg-card border-border cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 group"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg text-foreground">
            {t('identity.detail.graph.title')}
          </CardTitle>
        </div>
        <CardDescription className="text-muted-foreground">
          {t('identity.detail.graph.subtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md bg-muted/60 text-muted-foreground/80 flex h-24 w-full items-center justify-center">
          <TopEntityThumbnail variant={variantForSeed(identity.id)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 rounded-lg bg-muted">
            <p className="text-2xl font-bold text-foreground">{graphStats.hop1}</p>
            <p className="text-xs text-muted-foreground">
              {t('identity.detail.graph.directlyConnected')}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted">
            <p className="text-2xl font-bold text-foreground">{graphStats.hop2}</p>
            <p className="text-xs text-muted-foreground">
              {t('identity.detail.graph.twoStepsAway')}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 text-sm text-primary group-hover:translate-x-0.5 transition-transform">
          {opening ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <span>{t('identity.detail.graph.openGraph')}</span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
